"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    eyebrow: "Fakturascanning i Svendetid",
    h1a: "Scan fakturaen — ",
    h1b: "spring tasterierne",
    h1c: " over",
    sub: "Tag et billede af leverandørfakturaen efter turen til byggemarkedet, og Svendetid trækker automatisk hver varelinje — beskrivelse, antal, enhed, kostpris — direkte ind på jobbet. Ingen manuel indtastning.",
    cta: "Kom i gang – det er gratis",
    seePricing: "Se priser",

    trust2: "Gratis at komme i gang",
    trust3: "Dansk support",
    // hero mockup labels
    mockReport: "Fakturascanning",
    mockTitle: "Scannet faktura",
    heroSupplier: "STARK Byggecenter",
    heroScanLabel: "Scanner…",
    heroExtracted: "Udtrukket",
    heroLines: [
      { name: "Gipsplade 13mm", qty: "24 stk", price: "kr 1.176" },
      { name: "Skruer 4×40 (200)", qty: "3 pk", price: "kr 267" },
      { name: "Træregler 45×95", qty: "18 m", price: "kr 612" },
    ],
    heroToJob: "→ Tilføjet til job: Tagrenovering",
    // without / with
    cmpH2: "Materialer på jobbet — uden indtastningen",
    withoutLabel: "Uden Svendetid",
    withLabel: "Med Svendetid",
    without: [
      "Taste hver varelinje af fakturaen ind i hånden",
      "Kvitteringer der forsvinder i bilen eller bukselommen",
      "Materialer der aldrig kommer med på jobbet — tabt fortjeneste",
      "Aftener brugt på papirarbejde i stedet for derhjemme",
    ],
    with: [
      "Hele fakturaen aflæst på sekunder med ét billede",
      "Hver linje lagt på det rigtige job — antal, enhed og pris",
      "Materialeforbruget fanget, så intet glemmes i faktureringen",
      "Mindre papirarbejde, mere tid til arbejdet der tjener penge",
    ],
    // section intro
    introLabel: "Sådan virker det",
    introH2: "Fra kvittering til job på tre trin",
    introSub: "Scan. Udtræk. Færdig.",
    // three feature blocks
    blocks: [
      {
        label: "Scan fra mobilen",
        h3: "Tag billedet på vej ud af byggemarkedet",
        body: "Åbn appen, tag et billede af fakturaen eller kvitteringen — eller upload en PDF, leverandøren har sendt på mail. Svendetid genkender både trykte og mailede fakturaer, så du fanger materialerne mens du stadig står på parkeringspladsen.",
        chips: [
          { icon: "camera-outline", text: "Tag billede" },
          { icon: "document-text-outline", text: "Eller PDF" },
          { icon: "phone-portrait-outline", text: "Fra mobilen" },
        ],
        mock: "scan",
      },
      {
        label: "AI udtrækker linjerne",
        h3: "Beskrivelse, antal, enhed og pris — automatisk",
        body: "AI'en læser fakturaen og udtrækker hver enkelt varelinje: beskrivelse, antal, enhed, kostpris og total. Den genkender leverandøren og rydder op i teksten, så linjerne er pæne og klar til jobbet — ikke et rod af forkortelser.",
        chips: [
          { icon: "color-wand-outline", text: "Auto-udtræk" },
          { icon: "calculator-outline", text: "Antal & pris" },
          { icon: "checkmark-circle", text: "Tjek & ret" },
        ],
        mock: "extract",
      },
      {
        label: "Lander på jobbet",
        h3: "Materialerne ligger på jobbet — klar til fakturering",
        body: "Alle linjerne lægges direkte på det rigtige job som materialeforbrug. Sæt din avance, og de er klar til at komme med på kundens faktura. Det du købte, er det kunden betaler for — uden at en eneste linje bliver glemt.",
        chips: [
          { icon: "briefcase-outline", text: "På jobbet" },
          { icon: "trending-up-outline", text: "Tilføj avance" },
          { icon: "receipt-outline", text: "Klar til faktura" },
        ],
        mock: "job",
      },
    ],
    // scan mock labels
    mockScanSupplier: "STARK Byggecenter",
    mockScanTotal: "Total kr 2.055",
    mockScanStatus: "Scanner faktura…",
    // extract mock labels
    mockExtractTitle: "Udtrukne varelinjer",
    mockColItem: "Vare",
    mockColQty: "Antal",
    mockColPrice: "Pris",
    // job mock labels
    mockJobTitle: "Job · Tagrenovering",
    mockJobMaterials: "Materialer",
    mockJobAdded: "3 linjer tilføjet",
    mockJobMargin: "Avance +30%",
    mockJobReady: "Klar til faktura",
    // AI block
    aiLabel: "AI-drevet bogføring",
    aiH2a: "Svendetid AI læser ",
    aiH2b: "fakturaen for dig",
    aiBody: "Spørg om hvad som helst: »Hvad har vi købt af materialer til Nordby-jobbet?« eller »Hvilke fakturaer er scannet men ikke fordelt på et job?«. AI'en holder styr på indkøbene og fanger det, der mangler at blive faktureret videre.",
    aiCta: "Prøv Svend AI",
    aiQuestion: "Hvilke scannede fakturaer mangler at blive lagt på et job?",
    aiAnswerIntro: "2 fakturaer er scannet men ikke fordelt:",
    aiRow1: "STARK · 2.055 kr",
    aiRow1Val: "12 linjer",
    aiRow2: "Bauhaus · 884 kr",
    aiRow2Val: "5 linjer",
    aiAnswerOutro: "Vil du lægge dem på et job nu, så de kommer med på kundens faktura?",
    // feature grid
    gridH2: "Plus alt det, du skal bruge for at fange materialerne",
    grid: [
      { icon: "scan-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Scan eller upload", body: "Tag et billede af fakturaen, eller upload en PDF fra leverandørens mail. Begge dele aflæses automatisk." },
      { icon: "color-wand-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Automatisk udtræk", body: "AI'en udtrækker beskrivelse, antal, enhed, kostpris og total fra hver linje — uden manuel indtastning." },
      { icon: "briefcase-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Direkte på jobbet", body: "Linjerne lægges på det rigtige job som materialeforbrug, klar til at komme med på kundens faktura." },
      { icon: "trending-up-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Avance med ét tryk", body: "Læg din avance oven i kostprisen automatisk, så materialerne giver fortjeneste — ikke bare dækker udgiften." },
      { icon: "people-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Genkender leverandører", body: "Svendetid lærer dine leverandører at kende og rydder op i varetekster, så linjerne er pæne og ens." },
      { icon: "cloud-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Bilag gemt sikkert", body: "Den originale faktura gemmes som bilag på jobbet — sporbart og klar til bogføring og revision." },
    ],
    // platform
    platLabel: "Svendetid-platformen",
    platH2: "Fakturascanning er kun begyndelsen",
    platSub: "Fakturascanning er en del af platformen — jobs, tilbud, tid, kunder og fakturering i ét samlet system. Alt taler sammen.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Styr hvert job", href: "/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Tilbud", sub: "Vind flere opgaver", href: "/features/offer" },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Kalender", sub: "Planlæg sjakket", href: "/features/calendar" },
      { icon: "bar-chart-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Rapporter", sub: "Overblik der virker", href: "/features/reports" },
      { icon: "camera-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Kvalitetssikring", sub: "Fotos & underskrift", href: "/features/quality-control" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "Alle funktioner", sub: "Se hele platformen", href: "/features" },
    ],
    // security
    secLabel: "Sikkerhed hele vejen",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data i EU" },
      { icon: "chatbubbles-outline", text: "Support på dansk" },
    ],
    // FAQ
    faqH2: "Ofte stillede spørgsmål",
    faqs: [
      { q: "Hvilke fakturaer kan jeg scanne?", a: "Du kan tage et billede af en papirfaktura eller kvittering fra byggemarkedet, eller uploade en PDF, leverandøren har sendt på mail. Svendetid aflæser begge dele og udtrækker varelinjerne automatisk." },
      { q: "Hvad bliver udtrukket fra fakturaen?", a: "For hver linje udtrækkes beskrivelse, antal, enhed, kostpris og total — plus leverandør og fakturadato. Du kan altid tjekke og rette inden linjerne lægges på jobbet." },
      { q: "Lander materialerne automatisk på jobbet?", a: "Ja. Vælg jobbet, og alle linjerne lægges på som materialeforbrug. Sæt din avance, og de er klar til at komme med på kundens faktura — uden manuel indtastning." },
      { q: "Hvad hvis AI'en læser noget forkert?", a: "Du ser de udtrukne linjer inden de gemmes og kan rette antal, pris eller tekst med det samme. AI'en lærer dine leverandører at kende og bliver mere præcis over tid." },
      { q: "Hvad er gratis, og hvad koster penge?", a: "Du kan komme i gang gratis og prøve fakturascanning. Scanning i stor skala og avanceret leverandørgenkendelse er en del af de betalte planer — opgradér når du er klar." },
    ],
    // CTA band
    bandH2: "Scan fakturaen — og slip for indtastningen",
    bandBody: "Slut dig til håndværkere der fanger materialerne med ét billede. Intet betalingskort. Gratis for altid.",
  },
  en: {
    eyebrow: "Invoice scanning in Svendetid",
    h1a: "Scan the invoice — ",
    h1b: "skip the typing",
    h1c: "",
    sub: "Take a photo of the supplier invoice after the trip to the hardware store, and Svendetid automatically pulls every line item — description, quantity, unit, unit cost — straight onto the job. No manual entry.",
    cta: "Get started – it's free",
    seePricing: "See pricing",

    trust2: "Free to get started",
    trust3: "Danish support",
    mockReport: "Invoice scanning",
    mockTitle: "Scanned invoice",
    heroSupplier: "STARK Building Centre",
    heroScanLabel: "Scanning…",
    heroExtracted: "Extracted",
    heroLines: [
      { name: "Plasterboard 13mm", qty: "24 pcs", price: "kr 1,176" },
      { name: "Screws 4×40 (200)", qty: "3 pk", price: "kr 267" },
      { name: "Timber stud 45×95", qty: "18 m", price: "kr 612" },
    ],
    heroToJob: "→ Added to job: Roof renovation",
    cmpH2: "Materials on the job — without the typing",
    withoutLabel: "Without Svendetid",
    withLabel: "With Svendetid",
    without: [
      "Typing every invoice line in by hand",
      "Receipts that vanish in the van or a trouser pocket",
      "Materials that never make it onto the job — lost profit",
      "Evenings spent on paperwork instead of at home",
    ],
    with: [
      "The whole invoice read in seconds from one photo",
      "Every line placed on the right job — quantity, unit and price",
      "Material spend captured, so nothing's forgotten when invoicing",
      "Less paperwork, more time on the work that earns money",
    ],
    introLabel: "How it works",
    introH2: "From receipt to job in three steps",
    introSub: "Scan. Extract. Done.",
    blocks: [
      {
        label: "Scan from mobile",
        h3: "Take the photo on your way out of the store",
        body: "Open the app, snap a photo of the invoice or receipt — or upload a PDF the supplier emailed you. Svendetid recognises both printed and emailed invoices, so you capture the materials while you're still in the car park.",
        chips: [
          { icon: "camera-outline", text: "Take photo" },
          { icon: "document-text-outline", text: "Or PDF" },
          { icon: "phone-portrait-outline", text: "From mobile" },
        ],
        mock: "scan",
      },
      {
        label: "AI extracts the lines",
        h3: "Description, quantity, unit and price — automatically",
        body: "The AI reads the invoice and extracts every line item: description, quantity, unit, unit cost and total. It recognises the supplier and tidies up the text, so the lines are clean and ready for the job — not a mess of abbreviations.",
        chips: [
          { icon: "color-wand-outline", text: "Auto-extract" },
          { icon: "calculator-outline", text: "Qty & price" },
          { icon: "checkmark-circle", text: "Check & edit" },
        ],
        mock: "extract",
      },
      {
        label: "Lands on the job",
        h3: "Materials sit on the job — ready to invoice",
        body: "Every line is placed straight onto the right job as material spend. Set your markup, and they're ready to go on the customer's invoice. What you bought is what the customer pays for — without a single line being forgotten.",
        chips: [
          { icon: "briefcase-outline", text: "On the job" },
          { icon: "trending-up-outline", text: "Add markup" },
          { icon: "receipt-outline", text: "Ready to invoice" },
        ],
        mock: "job",
      },
    ],
    mockScanSupplier: "STARK Building Centre",
    mockScanTotal: "Total kr 2,055",
    mockScanStatus: "Scanning invoice…",
    mockExtractTitle: "Extracted line items",
    mockColItem: "Item",
    mockColQty: "Qty",
    mockColPrice: "Price",
    mockJobTitle: "Job · Roof renovation",
    mockJobMaterials: "Materials",
    mockJobAdded: "3 lines added",
    mockJobMargin: "Markup +30%",
    mockJobReady: "Ready to invoice",
    aiLabel: "AI-driven bookkeeping",
    aiH2a: "Svendetid AI reads ",
    aiH2b: "the invoice for you",
    aiBody: "Ask anything: “What materials did we buy for the Nordby job?” or “Which invoices are scanned but not assigned to a job?”. The AI keeps track of the purchases and catches what's left to invoice on.",
    aiCta: "Try Svendetid AI",
    aiQuestion: "Which scanned invoices still need to be placed on a job?",
    aiAnswerIntro: "2 invoices are scanned but not assigned:",
    aiRow1: "STARK · kr 2,055",
    aiRow1Val: "12 lines",
    aiRow2: "Bauhaus · kr 884",
    aiRow2Val: "5 lines",
    aiAnswerOutro: "Want to place them on a job now, so they make it onto the customer's invoice?",
    gridH2: "Plus everything you need to capture the materials",
    grid: [
      { icon: "scan-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Scan or upload", body: "Take a photo of the invoice, or upload a PDF from the supplier's email. Both are read automatically." },
      { icon: "color-wand-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Automatic extraction", body: "The AI extracts description, quantity, unit, unit cost and total from every line — no manual entry." },
      { icon: "briefcase-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Straight onto the job", body: "Lines are placed on the right job as material spend, ready to go on the customer's invoice." },
      { icon: "trending-up-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Markup in one tap", body: "Add your markup on top of the cost automatically, so materials turn a profit — not just cover the cost." },
      { icon: "people-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Recognises suppliers", body: "Svendetid learns your suppliers and tidies up item text, so the lines are clean and consistent." },
      { icon: "cloud-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Receipts stored safely", body: "The original invoice is kept as an attachment on the job — traceable and ready for bookkeeping and audit." },
    ],
    platLabel: "The Svendetid platform",
    platH2: "Invoice scanning is only the beginning",
    platSub: "Invoice scanning is part of the platform — jobs, quotes, time, customers and invoicing in one connected system. Everything talks to everything.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Run every job", href: "/en/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Quotes", sub: "Win more work", href: "/features/offer" },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Calendar", sub: "Plan the crew", href: "/en/features/calendar" },
      { icon: "bar-chart-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Reports", sub: "Overview that works", href: "/en/features/reports" },
      { icon: "camera-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Quality control", sub: "Photos & signature", href: "/en/features/quality-control" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "All features", sub: "See the whole platform", href: "/en/features" },
    ],
    secLabel: "Security all the way",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data in the EU" },
      { icon: "chatbubbles-outline", text: "Support in Danish" },
    ],
    faqH2: "Frequently asked questions",
    faqs: [
      { q: "Which invoices can I scan?", a: "You can take a photo of a paper invoice or receipt from the hardware store, or upload a PDF the supplier emailed you. Svendetid reads both and extracts the line items automatically." },
      { q: "What gets extracted from the invoice?", a: "For each line it extracts description, quantity, unit, unit cost and total — plus the supplier and invoice date. You can always check and edit before the lines are placed on the job." },
      { q: "Do the materials land on the job automatically?", a: "Yes. Pick the job, and every line is placed on it as material spend. Set your markup, and they're ready to go on the customer's invoice — without manual entry." },
      { q: "What if the AI reads something wrong?", a: "You see the extracted lines before they're saved and can fix quantity, price or text right away. The AI learns your suppliers and gets more accurate over time." },
      { q: "What's free and what costs money?", a: "You can get started free and try invoice scanning. Scanning at scale and advanced supplier recognition are part of the paid plans — upgrade when you're ready." },
    ],
    bandH2: "Scan the invoice — and skip the typing",
    bandBody: "Join trades businesses that capture materials with one photo. No payment card. Free forever.",
  },
} as const;

type TT = (typeof T)["da"] | (typeof T)["en"];

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
  padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column", gap: 15,
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em",
  textTransform: "uppercase", color: "var(--accent-strong)",
};

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14 }}>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      <span style={monoLabel}>{label}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

type Card = { icon: string; iconBg: string; iconColor: string; title: string; body: string };

function FeatureCard({ icon, iconBg, iconColor, title, body }: Card) {
  return (
    <div style={cardStyle}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={24} color={iconColor} />
      </div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{body}</p>
    </div>
  );
}

/* ------------------------------- Hero mockup ----------------------------- */
function HeroMock({ t }: { t: TT }) {
  const [visibleLines, setVisibleLines] = useState(0);
  const [phase, setPhase] = useState<"scanning" | "extracted">("scanning");

  useEffect(() => {
    // cycle: scan 2.4s → reveal lines one by one → pause → reset
    const cycle = () => {
      setVisibleLines(0);
      setPhase("scanning");
      const timers: ReturnType<typeof setTimeout>[] = [];
      // after first scan pass, start revealing
      timers.push(setTimeout(() => setPhase("extracted"), 2600));
      t.heroLines.forEach((_, i) => {
        timers.push(setTimeout(() => setVisibleLines(i + 1), 2800 + i * 380));
      });
      return timers;
    };
    let timers = cycle();
    const interval = setInterval(() => {
      timers.forEach(clearTimeout);
      timers = cycle();
    }, 6200);
    return () => { clearInterval(interval); timers.forEach(clearTimeout); };
  }, [t.heroLines]);

  return (
    <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "0.85fr 1.15fr", gap: 20, alignItems: "stretch", textAlign: "left" }}>
      {/* Scanned receipt */}
      <div style={{ position: "relative", background: "#FBFAF6", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 16px 36px rgba(16,33,27,0.14)", padding: "18px 16px", overflow: "hidden" }}>
        {/* animated scan line */}
        {phase === "scanning" && <div aria-hidden className="scan-line" />}
        {/* corner brackets */}
        {[["8px","8px","top","left"],["8px","8px","top","right"],["8px","8px","bottom","left"],["8px","8px","bottom","right"]].map(([t2,l,v,h],i) => (
          <div key={i} aria-hidden style={{
            position: "absolute",
            [v]: 10, [h]: 10,
            width: 14, height: 14,
            borderTop: v === "top" ? "2px solid var(--accent)" : "none",
            borderBottom: v === "bottom" ? "2px solid var(--accent)" : "none",
            borderLeft: h === "left" ? "2px solid var(--accent)" : "none",
            borderRight: h === "right" ? "2px solid var(--accent)" : "none",
            borderRadius: v === "top" && h === "left" ? "4px 0 0 0" : v === "top" && h === "right" ? "0 4px 0 0" : v === "bottom" && h === "left" ? "0 0 0 4px" : "0 0 4px 0",
            animation: "scan-corner-pulse 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.3}s`,
          }} />
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ width: 26, height: 26, borderRadius: 8, background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            <span style={{ display: "flex", animation: phase === "scanning" ? "wand-spin 1.2s ease-in-out infinite" : "none" }}>
              <Icon name="scan-outline" size={15} color="var(--accent-strong)" />
            </span>
          </span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: phase === "scanning" ? "var(--accent-strong)" : "var(--brand)", transition: "color 0.4s" }}>
            {phase === "scanning" ? t.heroScanLabel : t.heroExtracted}
          </span>
        </div>
        <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 15, color: "var(--green-900)" }}>{t.heroSupplier}</div>
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 9 }}>
          {[0.92, 0.7, 0.84, 0.55, 0.78].map((w, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div className="shimmer-bar" style={{ height: 7, flex: w }} />
              <div className="shimmer-bar" style={{ height: 7, width: 34 }} />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dashed var(--border)", display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: "var(--green-900)" }}>
          <span>{t.mockScanTotal}</span>
        </div>
      </div>

      {/* Extracted → job */}
      <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 16px 36px rgba(16,33,27,0.14)", padding: 18, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ width: 26, height: 26, borderRadius: 8, background: "var(--green-100)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            <span style={{ display: "flex", animation: phase === "extracted" ? "wand-spin 1.8s ease-in-out infinite" : "none" }}>
              <Icon name="color-wand-outline" size={15} color="var(--brand)" />
            </span>
          </span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--brand)" }}>{t.heroExtracted}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {t.heroLines.map((l, i) => (
            <div
              key={l.name}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 11px",
                background: i < visibleLines ? "var(--green-50)" : "var(--sage-50,#F5F7F5)",
                border: `1px solid ${i < visibleLines ? "var(--hairline)" : "transparent"}`,
                borderRadius: 10,
                opacity: i < visibleLines ? 1 : 0.25,
                transform: i < visibleLines ? "none" : "translateY(4px)",
                transition: "opacity 0.35s ease, transform 0.35s ease, background 0.3s",
              }}
            >
              <span style={{ animation: i < visibleLines ? "check-pop 0.4s ease forwards" : "none", display: "flex" }}>
                <Icon name="checkmark-circle" size={15} color={i < visibleLines ? "var(--brand)" : "var(--sage-300)"} />
              </span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--green-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</span>
              <span style={{ fontSize: 12, color: "var(--text-muted)", flex: "none" }}>{l.qty}</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600, color: "var(--green-900)", flex: "none", fontVariantNumeric: "tabular-nums" }}>{l.price}</span>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 14,
          display: "inline-flex", alignItems: "center", gap: 7, alignSelf: "flex-start",
          fontSize: 12, fontWeight: 600, color: "#fff",
          background: "var(--brand)", borderRadius: 9, padding: "8px 13px",
          boxShadow: "0 8px 18px rgba(47,107,72,0.28)",
          opacity: visibleLines >= t.heroLines.length ? 1 : 0,
          transform: visibleLines >= t.heroLines.length ? "none" : "translateY(6px)",
          transition: "opacity 0.5s ease 0.2s, transform 0.5s ease 0.2s",
        }}>
          <Icon name="briefcase-outline" size={14} color="#fff" />{t.heroToJob}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Feature-block mocks ------------------------- */
function ScanMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", maxWidth: 320, background: "#FBFAF6", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 18, position: "relative", overflow: "hidden" }}>
      {/* animated scan sweep */}
      <div aria-hidden className="scan-line" />
      {/* corner brackets */}
      {([["top","left"],["top","right"],["bottom","left"],["bottom","right"]] as const).map(([v,h],i) => (
        <div key={i} aria-hidden style={{
          position: "absolute", [v]: 10, [h]: 10,
          width: 16, height: 16,
          borderTop: v === "top" ? "2px solid var(--accent)" : "none",
          borderBottom: v === "bottom" ? "2px solid var(--accent)" : "none",
          borderLeft: h === "left" ? "2px solid var(--accent)" : "none",
          borderRight: h === "right" ? "2px solid var(--accent)" : "none",
          animation: "scan-corner-pulse 1.2s ease-in-out infinite",
          animationDelay: `${i * 0.3}s`,
        }} />
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ width: 28, height: 28, borderRadius: 8, background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ display: "flex", animation: "wand-spin 1.6s ease-in-out infinite" }}>
            <Icon name="receipt-outline" size={15} color="var(--accent-strong)" />
          </span>
        </span>
        <span style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 14, color: "var(--green-900)" }}>{t.mockScanSupplier}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {[0.95, 0.72, 0.88, 0.6].map((w, i) => (
          <div key={i} style={{ display: "flex", gap: 8 }}>
            <div className="shimmer-bar" style={{ height: 8, flex: w }} />
            <div className="shimmer-bar" style={{ height: 8, width: 36 }} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12, fontWeight: 600, color: "var(--accent-strong)", background: "var(--orange-100)", borderRadius: 10, padding: "10px 14px" }}>
        <span style={{ display: "flex", animation: "wand-spin 1.2s ease-in-out infinite" }}>
          <Icon name="scan-outline" size={15} color="var(--accent-strong)" />
        </span>
        {t.mockScanStatus}
      </div>
    </div>
  );
}

function ExtractMock({ t }: { t: TT }) {
  const [visibleLines, setVisibleLines] = useState(0);
  const [wandActive, setWandActive] = useState(true);

  useEffect(() => {
    const cycle = () => {
      setVisibleLines(0);
      setWandActive(true);
      const timers: ReturnType<typeof setTimeout>[] = [];
      t.heroLines.forEach((_, i) => {
        timers.push(setTimeout(() => setVisibleLines(i + 1), 600 + i * 500));
      });
      timers.push(setTimeout(() => setWandActive(false), 600 + t.heroLines.length * 500 + 200));
      return timers;
    };
    let timers = cycle();
    const interval = setInterval(() => { timers.forEach(clearTimeout); timers = cycle(); }, 5500);
    return () => { clearInterval(interval); timers.forEach(clearTimeout); };
  }, [t.heroLines]);

  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ display: "flex", animation: wandActive ? "wand-spin 1s ease-in-out infinite" : "none" }}>
          <Icon name="color-wand-outline" size={16} color="var(--brand)" />
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.mockExtractTitle}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "0 12px", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", paddingBottom: 8, borderBottom: "1px solid var(--hairline)" }}>
        <span>{t.mockColItem}</span><span style={{ textAlign: "right" }}>{t.mockColQty}</span><span style={{ textAlign: "right" }}>{t.mockColPrice}</span>
      </div>
      {t.heroLines.map((l, i) => (
        <div
          key={l.name}
          style={{
            display: "grid", gridTemplateColumns: "1fr auto auto", gap: "0 12px",
            fontSize: 13, padding: "10px 0", borderBottom: "1px solid var(--hairline)", alignItems: "center",
            opacity: i < visibleLines ? 1 : 0,
            transform: i < visibleLines ? "none" : "translateX(-8px)",
            transition: "opacity 0.38s ease, transform 0.38s ease",
          }}
        >
          <span style={{ fontWeight: 600, color: "var(--green-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ animation: i < visibleLines ? "check-pop 0.4s ease forwards" : "none", display: "flex", flex: "none" }}>
              <Icon name="checkmark-circle" size={13} color="var(--brand)" />
            </span>
            {l.name}
          </span>
          <span style={{ textAlign: "right", color: "var(--text-muted)" }}>{l.qty}</span>
          <span style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--green-900)", fontVariantNumeric: "tabular-nums" }}>{l.price}</span>
        </div>
      ))}
      <div style={{
        marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--brand)",
        opacity: visibleLines >= t.heroLines.length ? 1 : 0,
        transition: "opacity 0.4s ease 0.2s",
        animation: visibleLines >= t.heroLines.length ? "badge-slide 0.5s ease forwards" : "none",
      }}>
        <Icon name="color-wand-outline" size={14} color="var(--brand)" />{t.heroExtracted}
      </div>
    </div>
  );
}

function JobMock({ t }: { t: TT }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const cycle = () => {
      setStep(0);
      const t1 = setTimeout(() => setStep(1), 500);
      const t2 = setTimeout(() => setStep(2), 1100);
      const t3 = setTimeout(() => setStep(3), 1800);
      return [t1, t2, t3];
    };
    let timers = cycle();
    const interval = setInterval(() => { timers.forEach(clearTimeout); timers = cycle(); }, 5000);
    return () => { clearInterval(interval); timers.forEach(clearTimeout); };
  }, []);

  const rowStyle = (visible: boolean): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 10,
    opacity: visible ? 1 : 0,
    transform: visible ? "none" : "translateY(8px)",
    transition: "opacity 0.4s ease, transform 0.4s ease",
  });

  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: "1px solid var(--hairline)" }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--green-100)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ display: "flex", animation: step < 3 ? "wand-spin 1.4s ease-in-out infinite" : "none" }}>
            <Icon name="briefcase-outline" size={18} color="var(--brand)" />
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t.mockJobTitle}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{t.mockJobMaterials}</div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 600, color: "var(--brand)", background: "var(--tint-mint)", borderRadius: 8, padding: "4px 9px",
          opacity: step >= 3 ? 1 : 0, transition: "opacity 0.4s ease",
        }}>{t.mockJobReady}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
        <div style={rowStyle(step >= 1)}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="receipt-outline" size={15} color="var(--accent-strong)" /></span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.mockJobAdded}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="checkmark-circle" size={14} color="var(--brand)" />{t.heroExtracted}</span>
        </div>
        <div style={rowStyle(step >= 2)}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--tint-lilac)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="trending-up-outline" size={15} color="#4B3F8F" /></span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.mockJobMargin}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>+30%</span>
        </div>
        <div style={{
          ...rowStyle(step >= 3),
          marginTop: 2, justifyContent: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#fff",
          background: "var(--accent)", borderRadius: 10, padding: "10px 14px", boxShadow: "0 8px 18px rgba(194,65,12,0.24)",
        }}>
          <Icon name="receipt-outline" size={15} color="#fff" />{t.mockJobReady}
        </div>
      </div>
    </div>
  );
}

const MOCK_BG: Record<string, string> = {
  scan: "linear-gradient(155deg,var(--orange-100),#FFF7EF)",
  extract: "linear-gradient(155deg,var(--green-50),#EEF6F0)",
  job: "linear-gradient(155deg,var(--tint-lilac),#F4F2FB)",
};

function FeatureBlock({ block, reverse, t }: { block: TT["blocks"][number]; reverse: boolean; t: TT }) {
  const text = (
    <div style={{ order: reverse ? 2 : 1 }}>
      <div style={{ ...monoLabel, marginBottom: 14 }}>{block.label}</div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 32, lineHeight: 1.12, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{block.h3}</h3>
      <p style={{ fontSize: 17, color: "var(--text-muted)", lineHeight: 1.6, margin: "18px 0 0" }}>{block.body}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
        {block.chips.map((c) => (
          <span key={c.text} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 999, padding: "7px 13px" }}>
            <Icon name={c.icon} size={15} color="var(--brand)" />{c.text}
          </span>
        ))}
      </div>
    </div>
  );
  const visual = (
    <div style={{ order: reverse ? 1 : 2, background: MOCK_BG[block.mock], borderRadius: 28, padding: "clamp(28px,5%,48px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {block.mock === "scan" && <ScanMock t={t} />}
      {block.mock === "extract" && <ExtractMock t={t} />}
      {block.mock === "job" && <JobMock t={t} />}
    </div>
  );
  return (
    <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
      <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
        {text}
        {visual}
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "20px 22px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "var(--font-sans)" }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: "var(--green-900)" }}>{q}</span>
        <svg viewBox="0 0 24 24" width={18} height={18} fill="none" style={{ color: "var(--accent-strong)", flex: "none", transition: "transform .25s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div style={{ padding: "0 22px 22px", fontSize: 15, lineHeight: 1.6, color: "var(--text-muted)" }}>{a}</div>}
    </div>
  );
}

export default function InvoiceScanContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  const aiHref = lang === "en" ? "/en/ai" : "/ai";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`
        html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}
      `}</style>

      <style>{`
        @keyframes scan-sweep {
          0%   { top: 12px; opacity: 0; }
          5%   { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: calc(100% - 12px); opacity: 0; }
        }
        @keyframes scan-corner-pulse {
          0%,100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes wand-spin {
          0%   { transform: rotate(-15deg) scale(1); }
          50%  { transform: rotate(15deg) scale(1.18); }
          100% { transform: rotate(-15deg) scale(1); }
        }
        @keyframes line-appear {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes check-pop {
          0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
          70%  { transform: scale(1.25) rotate(4deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes shimmer-bar {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes badge-slide {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .scan-line {
          position: absolute; left: 16px; right: 16px; height: 2px;
          background: linear-gradient(90deg, transparent, var(--accent), transparent);
          box-shadow: 0 0 10px var(--accent), 0 0 20px var(--accent);
          animation: scan-sweep 2.4s ease-in-out infinite;
          pointer-events: none; z-index: 2;
        }
        .shimmer-bar {
          background: linear-gradient(90deg, var(--sage-200) 25%, var(--sage-100) 50%, var(--sage-200) 75%);
          background-size: 200% 100%;
          animation: shimmer-bar 1.6s ease-in-out infinite;
          border-radius: 4px;
        }
      `}</style>
      <LandingNav lang={lang} accent="blue" />

      {/* ===== HERO ===== */}
      <section style={{ background: "#FFFFFF", color: "var(--text-body)", paddingTop: 64 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "72px 32px 40px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, ...monoLabel, marginBottom: 22 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />{t.eyebrow}
          </div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 58, lineHeight: 1.03, letterSpacing: "-0.025em", margin: "0 auto", maxWidth: "16ch", color: "var(--green-900)", textWrap: "balance" }}>
            {t.h1a}<span style={{ fontStyle: "italic", color: "var(--brand)" }}>{t.h1b}</span>{t.h1c}
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: "60ch", margin: "24px auto 0", textWrap: "pretty" }}>{t.sub}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 34 }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-soft" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "var(--green-900)", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid var(--border)", background: "var(--surface-card)" }}>{t.seePricing}</Link>
          </div>
          <div style={{ display: "flex", gap: 22, justifyContent: "center", alignItems: "center", flexWrap: "wrap", marginTop: 20, fontSize: 13, color: "var(--text-muted)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust2}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust3}</span>
          </div>

          {/* HERO MOCKUP — scan → extract → job */}
          <div style={{ marginTop: 54, position: "relative" }}>
            <div
              style={{
                position: "relative",
                zIndex: 1,
                padding: "clamp(16px,3vw,32px)",
                borderRadius: 24,
                background: "linear-gradient(165deg, #FFF7EF 0%, #EEF6F0 55%, var(--green-100) 100%)",
                border: "1px solid var(--border)",
              }}
            >
              <HeroMock t={t} />
            </div>
          </div>
        </div>
      </section>

      {/* ===== WITHOUT / WITH ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "60ch", margin: "0 auto 40px" }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.08, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.cmpH2}</h2>
        </div>
        <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 20, padding: 30 }}>
            <div style={{ ...monoLabel, color: "var(--text-muted)", marginBottom: 18 }}>{t.withoutLabel}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.without.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--tint-rose)", borderRadius: "50%", padding: 3, color: "var(--danger)" }}><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: "var(--green-900)", border: "1px solid #2C3B35", borderRadius: 20, padding: 30, boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, ...monoLabel, color: "var(--accent)", marginBottom: 18 }}>
              <img src="/svendetid-logo.png" alt="" style={{ width: 18, height: 18 }} />{t.withLabel}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.with.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--brand)", borderRadius: "50%", padding: 3, color: "#fff" }}><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-on-hero)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION INTRO ===== */}
      <section style={{ maxWidth: 760, margin: "0 auto", padding: "40px 32px 8px", textAlign: "center" }}>
        <SectionEyebrow label={t.introLabel} />
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.introH2}</h2>
        <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0", maxWidth: "48ch" }}>{t.introSub}</p>
      </section>

      {/* ===== THREE FEATURE BLOCKS ===== */}
      {t.blocks.map((block, i) => (
        <FeatureBlock key={block.label} block={block} reverse={i === 1} t={t} />
      ))}

      {/* ===== AI BLOCK ===== */}
      <section style={{ padding: "64px 32px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(32px,5vw,64px)", boxShadow: "0 24px 60px rgba(16,33,27,0.22)", position: "relative", overflow: "hidden" }}>
          <div aria-hidden style={{ position: "absolute", top: "-30%", right: "-10%", width: 420, height: 420, background: "radial-gradient(circle, rgba(249,115,22,0.22), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center", position: "relative" }}>
            <div>
              <div style={{ ...monoLabel, color: "var(--accent)", marginBottom: 16 }}>{t.aiLabel}</div>
              <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--text-on-hero)" }}>{t.aiH2a}<span style={{ fontStyle: "italic", color: "#F4C8A6" }}>{t.aiH2b}</span></h2>
              <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", margin: "20px 0 0", maxWidth: "46ch" }}>{t.aiBody}</p>
              <div style={{ marginTop: 28 }}>
                <Link href={aiHref} className="btn-blue" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.aiCta}<Icon name="arrow-forward" size={18} /></Link>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid #2C3B35", borderRadius: 18, padding: 18, backdropFilter: "blur(4px)" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: "var(--text-on-hero)", background: "var(--brand)", borderRadius: "14px 14px 4px 14px", padding: "10px 14px", maxWidth: "80%" }}>{t.aiQuestion}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#FEC935,#FBA932)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="sparkles" size={15} color="#fff" /></span>
                <div style={{ background: "#151D1A", border: "1px solid #2C3B35", borderRadius: "4px 14px 14px 14px", padding: 14, fontSize: 13, color: "var(--text-on-hero)", lineHeight: 1.55 }}>
                  <div style={{ marginBottom: 10 }}>{t.aiAnswerIntro}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(47,107,72,0.18)", borderRadius: 8, marginBottom: 6 }}>
                    <span style={{ flex: 1 }}>{t.aiRow1}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#86D6B0" }}>{t.aiRow1Val}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(47,107,72,0.18)", borderRadius: 8 }}>
                    <span style={{ flex: 1 }}>{t.aiRow2}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#86D6B0" }}>{t.aiRow2Val}</span>
                  </div>
                  <div style={{ marginTop: 10, color: "var(--text-hero-muted)" }}>{t.aiAnswerOutro}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURE GRID ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.gridH2}</h2>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {t.grid.map((f) => <FeatureCard key={f.title} {...f} />)}
        </div>
      </section>

      {/* ===== PLATFORM ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "52ch", margin: "0 auto 36px" }}>
          <div style={{ ...monoLabel, marginBottom: 14 }}>{t.platLabel}</div>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.platH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0" }}>{t.platSub}</p>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {t.platform.map((p) => (
            <Link key={p.title} href={p.href} className="card-link" style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 16, textDecoration: "none", color: "inherit" }}>
              <span style={{ width: 38, height: 38, borderRadius: 10, background: p.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name={p.icon} size={19} color={p.iconColor} /></span>
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{p.title}</span>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{p.sub}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== SECURITY ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 32px 8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "18px 40px", padding: "28px 24px", background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 18 }}>
          <span style={{ ...monoLabel, color: "var(--text-muted)" }}>{t.secLabel}</span>
          {t.security.map((s) => (
            <span key={s.text} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--green-900)" }}><Icon name={s.icon} size={18} color="var(--brand)" />{s.text}</span>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section style={{ maxWidth: 780, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.faqH2}</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {t.faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 32px 72px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(40px,6vw,72px) 32px", textAlign: "center", position: "relative", overflow: "hidden", boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
          <div aria-hidden style={{ position: "absolute", bottom: "-40%", left: "50%", transform: "translateX(-50%)", width: 600, height: 400, background: "radial-gradient(circle, rgba(249,115,22,0.2), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <img src="/svendetid-logo.png" alt="" style={{ width: 48, height: 48, margin: "0 auto 22px", display: "block", position: "relative" }} />
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 44, lineHeight: 1.08, letterSpacing: "-0.02em", margin: "0 auto", maxWidth: "20ch", color: "#fff", position: "relative", textWrap: "balance" }}>{t.bandH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-hero-muted)", margin: "18px auto 0", maxWidth: "46ch", position: "relative" }}>{t.bandBody}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 30, position: "relative" }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/features/invoice-scanning" enHref="/en/features/invoice-scanning" />
    </div>
  );
}
